import Header from "./Header";
import AboutProductSection from "./AboutProductSection";
import ContactForm from "./ContactForm";
import React from "react";
import HowItWorksSection from "./HowItWorksSection";
import Metrics from "./Metrics";
import AboutAnalyticSolution from "./AboutAnalyticSolution";
import Industries from "./Industries";
import FadeInSection from "./FadeInSection";
import { useLanguage } from '../../components/hooks/LanguageContext';

function metrics(language) {
    const metricsText = {
        ru: [
            { value: "5+", text: "Гибких продуктов", isActive: true },
            { value: "100%", text: "Актуальность данных", isActive: false },
            { value: "10 лет", text: "Исторических данных", isActive: false }
        ],
        en: [
            { value: "5+", text: "Flexible Solutions", isActive: true },
            { value: "100%", text: "Accurate Data", isActive: false },
            { value: "10 Years", text: "Historical Insights", isActive: false }
        ]
    };
    return <Metrics metrics={metricsText[language]} />;
}

function aboutAnalyticSolution(language) {
    const features = {
        ru: [
            {
                icon: "mobi-mbri-clock",
                title: "Сбор, обработка и анализ миллионов данных",
                text: "Сотни факторов превращаются в осмысленную картину по каждой точке."
            },
            {
                icon: "mobi-mbri-growing-chart",
                title: "Прогнозирование прибыльности",
                text: "Вы получаете чёткий ответ: «стоит ли открываться именно здесь?»"
            },
            {
                icon: "mobi-mbri-edit-2",
                title: "Визуализация данных",
                text: "Решения быстрее, уверенность выше, аргументы — на вашей стороне."
            }
        ],
        en: [
            {
                icon: "mobi-mbri-clock",
                title: "Collecting, Processing, and Analysing Millions of Data Points",
                text: "Hundreds of factors are turned into actionable insights for every location."
            },
            {
                icon: "mobi-mbri-growing-chart",
                title: "Profitability Forecasting",
                text: "Get a clear answer: “Is this the right location for my business?”"
            },
            {
                icon: "mobi-mbri-edit-2",
                title: "Data Visualisation",
                text: "Make decisions faster, boost confidence, and have concrete arguments on your side."
            }
        ]
    };
    return <AboutAnalyticSolution features={features[language]} />;
}

function howItWorksSection(link, language) {
    const steps = {
        ru: [
            {
                number: "1",
                title: "Выберите местоположение",
                text: "Укажите точку на карте, для которой хотите получить анализ проходимости и доходности. Вы можете выбрать точку и настроить радиус вокруг нее."
            },
            {
                number: "2",
                title: "Выберите глубину анализа",
                text: "Вы можете получить удобную выгрузку, много важных данных собранных в одном месте, посчитаем за вас ключевые аспекты, которые помогут сделать вам выбор. Или же закажите глубокую аналитику и получите расширенный блок данных, а самое главное — узнаете сколько вы заработаете в этой точке."
            },
            {
                number: "3",
                title: "Примите взвешенное решение",
                text: "Ознакомьтесь с собранной информацией о локации и аналитикой по ней. Используйте полученные данные для принятия обоснованного решения о размещении бизнеса."
            }
        ],
        en: [
            {
                number: "1",
                title: "Choose Location",
                text: "Mark the point on the map for which you want footfall and profitability analysis. You can adjust the radius around it."
            },
            {
                number: "2",
                title: "Choose Depth of Analysis",
                text: "Get a convenient data export, many important metrics collected in one place. Or request deep analytics to receive an extended data block and understand your earnings potential."
            },
            {
                number: "3",
                title: "Make an Informed Decision",
                text: "Review the collected location data and analytics. Use it to make an informed decision about your business placement."
            }
        ]
    };
    return <HowItWorksSection steps={steps[language]} url={link} />;
}

function industries(language) {
    const items = {
        ru: [
            {
                iconClass: 'fi fi-rr-glass-cheers',
                title: 'Рестораны',
                description: 'Спрогнозируем, где открывать бургерную, чтобы туда ломились даже в дождь.'
            },
            {
                iconClass: 'fi fi-rr-house-building',
                title: 'Девелопмент',
                description: 'Найдём точку, где новостройка взлетит, а не будет пылиться с пустыми окнами.'
            },
            {
                iconClass: 'fi fi-rr-bank',
                title: 'Госсектор',
                description: 'Определим, где поставить остановку, чтобы бабушки не шли пешком три километра.'
            },
            {
                iconClass: 'fi fi-rr-shop',
                title: 'Маркетплейсы',
                description: 'Поставим ПВЗ туда, где за него скажет спасибо весь район.'
            }
        ],
        en: [
            {
                iconClass: 'fi fi-rr-glass-cheers',
                title: 'Restaurants',
                description: 'We’ll identify where to open your outlet to ensure strong footfall — rain or shine.'
            },
            {
                iconClass: 'fi fi-rr-house-building',
                title: 'Property Development',
                description: 'We’ll find the location where a new development will thrive, not stand empty.'
            },
            {
                iconClass: 'fi fi-rr-bank',
                title: 'Public Sector',
                description: 'We’ll locate optimal infrastructure, like bus stops, so that accessibility is maximised.'
            },
            {
                iconClass: 'fi fi-rr-shop',
                title: 'Marketplaces & Logistics',
                description: 'We’ll position your pickup points where the community will benefit most.'
            }
        ]
    };
    return <Industries items={items[language]} />;
}

const Home = () => {
    const howItWorksLink = "how-it-works";
    const { language } = useLanguage();

    return (
        <>
            <div className="bg-black text-white">
                <Header/>
                <AboutProductSection link={howItWorksLink}/>
                {metrics(language)}
            </div>

            <div className="bg-white text-black">
                <FadeInSection delay={0.3}>
                    {aboutAnalyticSolution(language)}
                </FadeInSection>

                <FadeInSection delay={0.4}>
                    {howItWorksSection(howItWorksLink, language)}
                </FadeInSection>

                <FadeInSection delay={0.5}>
                    {industries(language)}
                </FadeInSection>

                <FadeInSection delay={0.6}>
                    <ContactForm/>
                </FadeInSection>
            </div>
        </>
    );
};

Home.link = "/";

export default Home;
